import { createApolloClient } from '@everlutionsk/helpers-apollo-react';
import { Filejet, FilejetProvider, LruCache } from '@filejet/sdk/react';
import { Warning as WarningIcon } from '@mui/icons-material';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './src/app';
import { environment } from './src/environment';
import { initializeI18n } from './src/i18n';
import { ModeContextProvider } from './src/modeContext';

initializeI18n();

const root = createRoot(document.getElementById('root')!);

const apollo = createApolloClient({
  url: environment.apiUri,
  batchInterval: 10,
  // The permissions snapshot returns four parallel list fields whose
  // inner entries have no `id` — Apollo can't tell whether to merge or
  // replace when a refetch comes in shorter (e.g. when a save returns
  // just the org we touched). `merge: false` makes incoming arrays
  // replace cached arrays outright, which is exactly what we want here:
  // the server is the source of truth on every save.
  cacheConfig: {
    typePolicies: {
      AccountOrganisationPermission: {
        fields: {
          sectionVisibility: { merge: false },
          resolvedSectionVisibility: { merge: false },
          permissions: { merge: false },
          resolvedPermissions: { merge: false }
        }
      }
    }
  }
});

// Mirrors the main frontend's FileJet setup (packages/frontend/index.tsx)
// so the account branding tab can render <Img> previews of tenant assets.
const filejet = new Filejet({
  domain: environment.uploadsDomain,
  Img: {
    dpiScale: [1, 1.5, 2],
    placeholderNode: <div style={{ background: '#eeeded', width: '100%', height: '100%' }}></div>,
    errorNode: (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          backgroundColor: `rgba(255,255,255, 0.4)`
        }}
      >
        <WarningIcon color="primary" />
        Failed to load
      </div>
    )
  },
  ThumbhashImg: {
    cache: new LruCache({ maxSize: 512 }),
    intersectRootMargin: 100
  }
});

root.render(
  <ModeContextProvider>
    <FilejetProvider filejet={filejet}>
      <App apollo={apollo} />
    </FilejetProvider>
  </ModeContextProvider>
);
